// schema.org structured data (JSON-LD) for search engines and AI answer engines. Describe only what the page shows.

import type { FC, ReactNode } from 'react'

import Head from 'next/head'
import { siteOrigin } from '@/components/generals/seo'

interface JsonLdProps {
    /** One script per id, so a page never sends the same block twice. */
    id: string
    data: Record<string, unknown>
}

interface Crumb {
    name: string
    path: string
}

/** JSON for a script tag: `<` is escaped so a value can never close the tag. */
export const toJsonLd = (data: unknown): string => JSON.stringify(data).replace(/</g, '\\u003c')

/** The stable id of Suvaani itself, shared by every page so all blocks point at one organisation. */
export const organizationRef = (): { '@id': string } => ({ '@id': `${siteOrigin()}/#organization` })

export const breadcrumbList = (items: Crumb[]): Record<string, unknown> => ({
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: `${siteOrigin()}${item.path === '/' ? '/' : item.path}`
    }))
})

/** Only for questions and answers that are visible on the same page. */
export const faqPage = (items: { q: string, a: string }[]): Record<string, unknown> => ({
    '@type': 'FAQPage',
    mainEntity: items.map(item => ({
        '@type': 'Question',
        name: item.q,
        acceptedAnswer: { '@type': 'Answer', text: item.a }
    }))
})

export const JsonLd: FC<JsonLdProps> = ({ id, data }): ReactNode => (
    <Head>
        <script
            key={`ld-${id}`}
            type='application/ld+json'
            dangerouslySetInnerHTML={{ __html: toJsonLd({ '@context': 'https://schema.org', ...data }) }}
        />
    </Head>
)
